import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  QrCode, Scan, ShoppingBag, Shield, Wrench, Lock, Smartphone, 
  ChevronRight, Download, Check, RefreshCw, FileText, Clock, 
  ClipboardList, AlertTriangle, Settings, History, Mail, Phone,
  TrendingUp, Target, FileSpreadsheet, Users, Zap, Factory,
  UserCheck, KeyRound, BookOpen, DollarSign
} from "lucide-react";
import { toast } from "sonner";
import { useAppUpdate } from "../hooks/useAppUpdate";

const LandingPage = () => {
  const navigate = useNavigate();
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const { updateAvailable, appVersion, refreshApp } = useAppUpdate();

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Check if install prompt is already available
    if (window.deferredPrompt) {
      setCanInstall(true);
    }

    // Listen for the install prompt becoming ready
    const handleInstallReady = () => {
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setCanInstall(false);
      toast.success("App has been installed!");
    };

    window.addEventListener('pwainstallready', handleInstallReady);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('pwainstallready', handleInstallReady);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallPWA = async () => {
    if (window.deferredPrompt) {
      window.deferredPrompt.prompt();
      const { outcome } = await window.deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        console.log('PWA installed');
        setIsInstalled(true);
        setCanInstall(false);
      }
      window.deferredPrompt = null;
    } else if (isInstalled) {
      toast.info("App is already installed on your device!");
    } else {
      // Show instructions based on browser
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes('iphone') || ua.includes('ipad')) {
        toast.info("Tap the Share button, then 'Add to Home Screen'", { duration: 5000 });
      } else if (ua.includes('android')) {
        toast.info("Tap the menu (⋮) → 'Add to Home Screen' or 'Install App'", { duration: 5000 });
      } else {
        toast.info("Click the install icon in your browser's address bar, or use the browser menu to install", { duration: 5000 });
      }
    }
  };

  // USP Cards Data - In exact order as specified
  // Tints assigned by meaning, ensuring no adjacent cards share the same tint
  const uspCards = [
    {
      icon: TrendingUp,
      title: "Instant Maintenance History",
      desc: "View complete maintenance history, previous breakdowns, repairs, and maintenance activities instantly by scanning the machine QR code.",
      cardTint: "homepage-card-cyan",
      iconTint: "homepage-icon-cyan"
    },
    {
      icon: Target,
      title: "Capture Only Essential Information",
      desc: "A clean and intuitive interface that records only the information maintenance teams actually need—without unnecessary fields, lengthy forms, or complicated workflows.",
      cardTint: "homepage-card-mint",
      iconTint: "homepage-icon-mint"
    },
    {
      icon: FileSpreadsheet,
      title: "One-Click Excel & PDF Export",
      desc: "Export complete maintenance logs and maintenance history into professionally formatted Excel or PDF reports with a single click, making audits, management reviews, and record keeping simple.",
      cardTint: "homepage-card-lavender",
      iconTint: "homepage-icon-lavender"
    },
    {
      icon: Users,
      title: "No Dedicated IT Team Required",
      desc: "Designed for factories to manage independently without requiring a dedicated IT department or software administrator.",
      cardTint: "homepage-card-amber",
      iconTint: "homepage-icon-amber"
    },
    {
      icon: Zap,
      title: "Ready in Hours, Not Days",
      desc: "Start digitizing maintenance within hours—without lengthy implementation, complex configuration, or expensive consultants.",
      cardTint: "homepage-card-cyan",
      iconTint: "homepage-icon-cyan"
    },
    {
      icon: Factory,
      title: "Machine-First, Not Software-First",
      desc: "Every maintenance activity starts at the machine. Simply scan the QR code to report a breakdown, view machine information, access maintenance history, or update maintenance records.",
      cardTint: "homepage-card-blue",
      iconTint: "homepage-icon-blue"
    },
    {
      icon: Smartphone,
      title: "No App Installation for Operators",
      desc: "Operators can report machine breakdowns instantly by scanning a QR code using any mobile browser—no app download required.",
      cardTint: "homepage-card-mint",
      iconTint: "homepage-icon-mint"
    },
    {
      icon: KeyRound,
      title: "No Operator Login Required",
      desc: "Operators do not need usernames or passwords to report maintenance requests, making reporting fast and effortless.",
      cardTint: "homepage-card-amber",
      iconTint: "homepage-icon-amber"
    },
    {
      icon: BookOpen,
      title: "Simple Digital Maintenance Records",
      desc: "Replace paper registers, notebooks, and scattered Excel sheets with centralized digital maintenance records for every machine.",
      cardTint: "homepage-card-lavender",
      iconTint: "homepage-icon-lavender"
    },
    {
      icon: DollarSign,
      title: "Simple Per-Machine Pricing",
      desc: "Pay based on the number of machines instead of the number of users, making the system affordable and scalable for growing factories.",
      cardTint: "homepage-card-blue",
      iconTint: "homepage-icon-blue"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F1F5F9] w-full">
      {/* Header - stays white */}
      <header className="w-full px-4 md:px-8 lg:px-16 py-4 flex items-center justify-between border-b border-[#E2E8F0] bg-white">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-[#2563EB] rounded-xl flex items-center justify-center">
            <Wrench className="w-6 h-6 text-white" />
          </div>
          <span className="text-xl font-bold text-[#0F172A]" style={{ fontFamily: 'Manrope' }}>
            QuickQrId
          </span>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleInstallPWA}
          className={`border-[#E2E8F0] hover:bg-[#F8FAFC] ${isInstalled ? 'text-green-600 border-green-200' : 'text-[#475569]'}`}
          data-testid="install-btn"
        >
          {isInstalled ? (
            <>
              <Check className="w-4 h-4 mr-2" />
              Installed
            </>
          ) : canInstall ? (
            <>
              <Download className="w-4 h-4 mr-2" />
              Install Now
            </>
          ) : (
            <>
              <Smartphone className="w-4 h-4 mr-2" />
              Install App
            </>
          )}
        </Button>
      </header>

      {/* Hero Section */}
      <section className="w-full px-4 md:px-8 lg:px-16 py-16 md:py-24 lg:py-32 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 text-[#2563EB] px-4 py-2 rounded-full text-sm mb-6">
            <Factory className="w-4 h-4" />
            Machine Maintenance QR System
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#0F172A] mb-6 leading-tight" style={{ fontFamily: 'Manrope' }}>
            The Simplest Way to<br />
            <span className="text-[#2563EB]">Digitize Factory Maintenance</span>
          </h1>
          
          <p className="text-lg md:text-xl text-[#475569] mb-12 max-w-4xl mx-auto leading-relaxed">
            QuickQrId helps factories replace paper registers, WhatsApp messages, and Excel sheets 
            with a simple QR-based maintenance system—without the complexity of traditional CMMS.
          </p>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              onClick={() => navigate('/scan')}
              size="lg"
              className="h-14 md:h-16 px-8 md:px-12 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-lg md:text-xl font-semibold rounded-xl"
              data-testid="scan-btn"
            >
              <Scan className="w-5 h-5 md:w-6 md:h-6 mr-2" />
              Scan QR Code
            </Button>
            
            <Button
              onClick={() => navigate('/buy')}
              size="lg"
              variant="outline"
              className="h-14 md:h-16 px-8 md:px-12 border-2 border-[#22C55E] text-[#22C55E] hover:bg-[#22C55E]/10 text-lg md:text-xl font-semibold rounded-xl"
              data-testid="buy-btn"
            >
              <ShoppingBag className="w-5 h-5 md:w-6 md:h-6 mr-2" />
              Get QR Codes
            </Button>
          </div>

          {/* User Login Link */}
          <p className="text-[#475569]">
            Already have an account?{" "}
            <button 
              onClick={() => navigate('/login')}
              className="text-[#2563EB] hover:underline font-medium"
            >
              Login here
            </button>
          </p>
        </div>
      </section>

      {/* Why Choose QuickQrId Section */}
      <section className="w-full px-4 md:px-8 lg:px-16 py-16 md:py-24 bg-[#F1F5F9]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] text-center mb-4" style={{ fontFamily: 'Manrope' }}>
            Why Choose QuickQrId?
          </h2>
          <p className="text-[#475569] text-center mb-12 text-lg max-w-2xl mx-auto">
            Built specifically for manufacturing companies who need simple, effective maintenance tracking
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {uspCards.map((item, index) => (
              <Card key={index} className={`${item.cardTint} border h-full`}>
                <CardContent className="p-6 md:p-8">
                  <div className={`w-14 h-14 ${item.iconTint} rounded-xl flex items-center justify-center mb-5`}>
                    <item.icon className="w-7 h-7" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-[#0F172A] mb-3">{item.title}</h3>
                  <p className="text-sm md:text-base text-[#475569] leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="w-full px-4 md:px-8 lg:px-16 py-16 md:py-24 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] text-center mb-4" style={{ fontFamily: 'Manrope' }}>
            How It Works
          </h2>
          <p className="text-[#475569] text-center mb-12 text-lg">4 Simple Steps</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {[
              { step: 1, icon: ShoppingBag, title: "Get QR Code", desc: "Get a QR code sticker for your machine", cardTint: "homepage-card-blue", iconTint: "homepage-icon-blue" },
              { step: 2, icon: Wrench, title: "Stick on Machine", desc: "Attach the QR code sticker to the machine", cardTint: "homepage-card-amber", iconTint: "homepage-icon-amber" },
              { step: 3, icon: Scan, title: "Scan to View", desc: "Anyone can scan to view machine details", cardTint: "homepage-card-cyan", iconTint: "homepage-icon-cyan" },
              { step: 4, icon: Lock, title: "Update with PIN", desc: "Only authorized users can edit using PIN", cardTint: "homepage-card-mint", iconTint: "homepage-icon-mint" },
            ].map((item, index) => (
              <div key={item.step} className="relative">
                <Card className={`${item.cardTint} border h-full`}>
                  <CardContent className="p-6 md:p-8 text-center">
                    <div className={`w-14 h-14 md:w-16 md:h-16 ${item.iconTint} rounded-full flex items-center justify-center mx-auto mb-4`}>
                      <item.icon className="w-7 h-7 md:w-8 md:h-8" />
                    </div>
                    <div className="text-xs md:text-sm text-[#2563EB] font-semibold mb-2">STEP {item.step}</div>
                    <h3 className="text-lg md:text-xl font-bold text-[#0F172A] mb-2">{item.title}</h3>
                    <p className="text-sm md:text-base text-[#475569]">{item.desc}</p>
                  </CardContent>
                </Card>
                {index < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                    <ChevronRight className="w-8 h-8 text-[#CBD5E1]" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Information is Stored */}
      <section className="w-full px-4 md:px-8 lg:px-16 py-16 md:py-24 bg-[#F1F5F9]">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] text-center mb-12" style={{ fontFamily: 'Manrope' }}>
            What Information is Stored
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* Machine Details */}
            <Card className="homepage-card-blue border">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 homepage-icon-blue rounded-lg flex items-center justify-center">
                    <Wrench className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A]">Machine Details</h3>
                </div>
                <ul className="space-y-2 text-[#475569]">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Machine Name & ID
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Type & Location
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Installation Date
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Operator & Technician Info
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Current Status
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Maintenance Logs */}
            <Card className="homepage-card-amber border">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 homepage-icon-amber rounded-lg flex items-center justify-center">
                    <ClipboardList className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A]">Maintenance Logs</h3>
                </div>
                <ul className="space-y-2 text-[#475569]">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Date of Service
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Issue Observed
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Action Taken
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Parts Replaced
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Next Service Date
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Instructions */}
            <Card className="homepage-card-mint border">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 homepage-icon-mint rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A]">Operating Instructions</h3>
                </div>
                <p className="text-[#475569]">
                  Step-by-step instructions for operating the machine safely and efficiently. 
                  Available to anyone who scans the QR code.
                </p>
              </CardContent>
            </Card>

            {/* Safety Precautions */}
            <Card className="homepage-card-rose border">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 homepage-icon-rose rounded-lg flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0F172A]">Safety Precautions</h3>
                </div>
                <p className="text-[#475569]">
                  Important safety warnings, do's and don'ts, and precautions 
                  that operators must follow while using the machine.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="w-full px-4 md:px-8 lg:px-16 py-16 md:py-24 bg-[#2563EB]">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope' }}>
            Ready to Get Started?
          </h2>
          <p className="text-white/80 mb-10 text-lg md:text-xl">
            Get QR codes for your machines and start tracking maintenance today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/buy')}
              size="lg"
              className="h-14 md:h-16 px-8 md:px-12 bg-white text-[#2563EB] hover:bg-gray-100 text-lg md:text-xl font-semibold rounded-xl"
            >
              <ShoppingBag className="w-5 h-5 md:w-6 md:h-6 mr-2" />
              Get QR Codes
            </Button>
            <Button
              onClick={() => navigate('/scan')}
              size="lg"
              variant="outline"
              className="h-14 md:h-16 px-8 md:px-12 border-2 border-white text-white hover:bg-white/10 text-lg md:text-xl font-semibold rounded-xl"
            >
              <Scan className="w-5 h-5 md:w-6 md:h-6 mr-2" />
              Scan Existing QR
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full px-4 md:px-8 lg:px-16 py-8 bg-[#F8FAFC] border-t border-[#E2E8F0]">
        <div className="max-w-6xl mx-auto">
          {/* Contact Info */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6 pb-6 border-b border-[#E2E8F0]">
            <a 
              href="mailto:quickqridinfo@gmail.com" 
              className="flex items-center gap-2 text-[#475569] hover:text-[#2563EB] transition-colors"
            >
              <Mail className="w-4 h-4 text-[#2563EB]" />
              <span className="text-sm">quickqridinfo@gmail.com</span>
            </a>
            <a 
              href="tel:+919209908344" 
              className="flex items-center gap-2 text-[#475569] hover:text-[#2563EB] transition-colors"
            >
              <Phone className="w-4 h-4 text-[#2563EB]" />
              <span className="text-sm">+91 9209908344</span>
            </a>
          </div>
          
          {/* Copyright & Branding */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Wrench className="w-6 h-6 text-[#2563EB]" />
              <span className="text-[#0F172A] font-semibold">QuickQrId</span>
            </div>
            <div className="flex items-center gap-4">
              <p className="text-[#475569] text-sm">
                © 2026 QuickQrId - Machine Maintenance QR System
              </p>
              {updateAvailable ? (
                <button
                  onClick={refreshApp}
                  className="flex items-center gap-1 text-[#2563EB] text-xs hover:underline"
                  data-testid="update-btn"
                >
                  <RefreshCw className="w-3 h-3" />
                  Update Available
                </button>
              ) : (
                <span className="text-[#94A3B8] text-xs" data-testid="app-version">v{appVersion}</span>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
